import { useParams } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonSecondary,
  PermissionDenied,
  PermissionEnum,
  Popup,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
} from '@ui/src';
import { useAppSelector } from '@store';
import useDictionaryItemForm from './hooks/use-form';
import DictionaryItemFormContent from '../../../components/dictionaries/form';

import cn from '../styles.module.css';

const DictionaryItemForm = () => {
  const { activePermissions } = useAppSelector((state) => state.root);
  const { dictionaryItem, takeCount } = useAppSelector(
    (state) => state.dictionaries,
  );

  const { dictionaryId, itemId } = useParams();

  const {
    panelIsOpen,
    setDirtyPopupIsOpen,
    dirtyPopuplIsOpen,
    deletePopupVisible,
    setDeletePopupVisible,
    globalError,
    deleteError,
    control,
    errors,
    isDirty,
    setValue,
    createInProgress,
    updateInProgress,
    deleteInProgress,
    onSubmit,
    onDelete,
    handleClose,
    isLoading,
    isFetching,
    onCreate,
    onDuplicateClick,
    duplicate,
  } = useDictionaryItemForm({
    dictionaryId,
    itemId,
    dictionaryItem,
    takeCount,
    activePermissions,
  });

  let content = <div />;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (!isLoading && !isFetching) {
    if (
      (!itemId &&
        activePermissions?.includes(
          PermissionEnum.DICTIONARIES_ITEMS_CREATE_ACTION,
        )) ||
      (itemId &&
        activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST))
    ) {
      content = (
        <DictionaryItemFormContent
          control={control}
          setValue={setValue}
          errors={errors}
          isEditable={Boolean(
            (!itemId &&
              activePermissions?.includes(
                PermissionEnum.DICTIONARIES_ITEMS_CREATE_ACTION,
              )) ||
            (itemId &&
              activePermissions.includes(
                PermissionEnum.DICTIONARIES_ITEMS_UPDATE_ACTION,
              ) &&
              !dictionaryItem?.deletedAt),
          )}
          deletedAt={dictionaryItem?.deletedAt}
        />
      );
    } else {
      content = <PermissionDenied />;
    }
  }

  let titleText = 'Dictionary value';

  if (!itemId) {
    titleText = `Add ${titleText}`;
  } else if (itemId && !dictionaryItem?.deletedAt) {
    titleText = `Edit ${titleText}`;
  }

  return (
    <>
      <SidePanel
        title={titleText}
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        sidepanelExpandedKey="psp-partner-link-client"
        onClose={() => {
          if (isDirty) {
            setDirtyPopupIsOpen(true);
          } else {
            handleClose();
          }
        }}
        errorSidepanel={globalError}
        footer={
          <>
            {!isLoading &&
              !isFetching &&
              ((!itemId &&
                activePermissions?.includes(
                  PermissionEnum.DICTIONARIES_ITEMS_CREATE_ACTION,
                )) ||
                (itemId &&
                  activePermissions?.includes(
                    PermissionEnum.DICTIONARIES_ITEMS_UPDATE_ACTION,
                  ) &&
                  !dictionaryItem?.deletedAt)) && (
                <ButtonPrimary
                  text={
                    updateInProgress || createInProgress
                      ? 'Loading...'
                      : 'Save value'
                  }
                  disabled={
                    updateInProgress ||
                    createInProgress ||
                    (!isDirty && !duplicate)
                  }
                  onClick={onSubmit}
                  bold
                />
              )}
            {!itemId &&
              activePermissions?.includes(
                PermissionEnum.DICTIONARIES_ITEMS_CREATE_ACTION,
              ) && (
                <ButtonSecondary
                  text={
                    updateInProgress || createInProgress
                      ? 'Loading...'
                      : 'Save & add another'
                  }
                  className={`button-white ${cn.deleteDictioanryItemButton}`}
                  disabled={
                    updateInProgress ||
                    createInProgress ||
                    (!isDirty && !duplicate)
                  }
                  onClick={onCreate}
                  bold
                />
              )}
            {itemId &&
              dictionaryItem?.deletedAt &&
              activePermissions?.includes(
                PermissionEnum.DICTIONARIES_ITEMS_CREATE_ACTION,
              ) && (
                <ButtonSecondary
                  text="Duplicate"
                  onClick={onDuplicateClick}
                  bold
                />
              )}
            {itemId &&
              !dictionaryItem?.deletedAt &&
              activePermissions?.includes(
                PermissionEnum.DICTIONARIES_ITEMS_DELETE_ACTION,
              ) &&
              !isLoading &&
              !isFetching && (
                <ButtonSecondary
                  text="Delete"
                  className={`${cn.deleteDictioanryItemButton} button-warning button-white`}
                  onClick={() => setDeletePopupVisible(true)}
                  bold
                />
              )}
            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupIsOpen(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="Are you sure you want to delete this value?"
        description=""
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : `Yes, Delete`}
        okButtonDisabled={deleteInProgress}
        isVisible={deletePopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      >
        <div>
          {deleteError && (
            <div className={cn.deleteDictionaryItemError}>{deleteError}</div>
          )}
        </div>
      </Popup>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupIsOpen(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopuplIsOpen}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default DictionaryItemForm;
